'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

// Platform IconButton — square, icon-only. Separate from Button so the
// TypeScript surface can REQUIRE `aria-label`: a consumer cannot ship an
// icon-only button without a screen-reader name. Tooltip is rendered via
// native `title` for now; Phase 3 can swap a Radix tooltip behind the
// same `tooltip` prop without changing the API.

const iconButtonVariants = cva(
  [
    'inline-flex items-center justify-center shrink-0',
    'transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
    'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    '[&_svg]:pointer-events-none',
  ].join(' '),
  {
    variants: {
      variant: {
        ghost:
          'bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
        secondary:
          'border border-border bg-transparent text-foreground hover:bg-muted',
        destructive: 'bg-transparent text-destructive hover:bg-destructive/10',
        primaryFill: 'bg-primary text-primary-foreground hover:bg-primary/90',
      },
      size: {
        xs: 'h-7 w-7',
        sm: 'h-8 w-8',
        md: 'h-9 w-9',
      },
      rounded: {
        none: 'rounded-none',
        sm: 'rounded-sm',
        md: 'rounded-md',
      },
    },
    defaultVariants: {
      variant: 'ghost',
      size: 'sm',
      rounded: 'none',
    },
  }
)

const iconSizeMap = { xs: 12, sm: 14, md: 16 } as const

export interface IconButtonProps
  extends
    Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      'aria-label' | 'children'
    >,
    VariantProps<typeof iconButtonVariants> {
  // REQUIRED — TypeScript-enforced. No accessible-name escape hatch.
  'aria-label': string
  icon: React.ComponentType<{ size?: number; className?: string }>
  loading?: boolean
  // Native `title` tooltip in Phase 1. Same prop name will swap to a real
  // tooltip primitive in Phase 3 without breaking callers.
  tooltip?: string
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      variant,
      size = 'sm',
      rounded,
      loading = false,
      icon: Icon,
      tooltip,
      disabled,
      ...props
    },
    ref
  ) => {
    const isInert = disabled || loading
    const renderedIconSize = iconSizeMap[size ?? 'sm']

    return (
      <button
        ref={ref}
        type={props.type ?? 'button'}
        disabled={isInert}
        aria-busy={loading || undefined}
        title={tooltip}
        className={cn(
          iconButtonVariants({ variant, size, rounded }),
          loading && 'cursor-wait',
          className
        )}
        {...props}
      >
        {loading ? (
          <Loader2 size={renderedIconSize} className='animate-spin' />
        ) : (
          <Icon size={renderedIconSize} />
        )}
      </button>
    )
  }
)

IconButton.displayName = 'IconButton'
